import * as PIXI from "pixi.js";
import hamStop1Image from "../assets/ham1.png";
import hamStop2Image from "../assets/ham2.png";
import hamRunning1Image from "../assets/ham_running1.png";
import hamRunning2Image from "../assets/ham_running2.png";
import { charaSize, screenWidth } from "../constants";
import {
  CollisionManager,
  createRect,
  Rect,
} from "../managers/collisionManager";
import { InputManager } from "../managers/inputManager";
import { LoadManager } from "../managers/loadManager";
import { ScrollManager } from "../managers/scrollManager";
import { SoundManager } from "../managers/soundManager";
import { clamp } from "../utils/clamp";

export function loadPlayerAssets(loadManager: LoadManager) {
  return {
    hamStop1: loadManager.loadTexture(hamStop1Image),
    hamStop2: loadManager.loadTexture(hamStop2Image),
    hamRunning1: loadManager.loadTexture(hamRunning1Image),
    hamRunning2: loadManager.loadTexture(hamRunning2Image),
  } as const;
}

const gravity = 1 / 300;
const jumpInitialVelocity = -gravity * 400;

const maxVelocity = screenWidth / 1500; // dots/ms

type SetupPlayerProps = Readonly<{
  playerAssets: ReturnType<typeof loadPlayerAssets>;
  parent: PIXI.Container;
  ticker: PIXI.Ticker;
  soundManager: SoundManager;
  inputManager: InputManager;
  collisionManager: CollisionManager;
  scrollManager: ScrollManager;
  x: number;
  y: number;
  stageWidth: number;
}>;
export function setupPlayer({
  playerAssets,
  parent,
  ticker,
  inputManager,
  soundManager,
  collisionManager,
  scrollManager,
  x: initialX,
  y: initialY,
  stageWidth,
}: SetupPlayerProps) {
  const player = new PIXI.Sprite(playerAssets.hamStop1.get());
  player.width = charaSize;
  player.height = charaSize;
  player.anchor.set(0.5, 0.5);

  let x = initialX;
  let y = initialY;
  let velocityX = 0;
  let velocityY = 0;
  let toRight = true;
  let rect: Rect = createRect(
    "player",
    x - charaSize / 2,
    y - charaSize / 2,
    charaSize,
    charaSize,
  );

  const top = () => y - charaSize / 2;
  const bottom = () => y + charaSize / 2;

  const onTick = () => {
    player.x = x;
    player.y = y;
    collisionManager.remove(rect);
    rect = createRect(
      "player",
      x - charaSize / 2,
      y - charaSize / 2,
      charaSize,
      charaSize,
    );
    collisionManager.add(rect);
    const bottomBound = collisionManager.bottomBoundOf("scaffold", rect);
    const topBound = collisionManager.topBoundOf("scaffold", rect);
    let floating = bottom() < bottomBound;
    if (!floating && inputManager.jump) {
      soundManager.playJumpSE();
      velocityY = jumpInitialVelocity;
      floating = true;
    }
    if (floating) {
      y += velocityY * ticker.deltaMS;
      velocityY += gravity * ticker.deltaMS;
    }
    if (top() < topBound) {
      y = topBound + charaSize / 2;
      velocityY = 0;
    }
    if (velocityY > 0 && bottom() > bottomBound) {
      y = bottomBound - charaSize / 2;
      velocityY = 0;
    }
    if (!floating) {
      velocityX = maxVelocity * inputManager.xAxis;
    } else if (
      velocityX === 0 ||
      (inputManager.xAxis !== 0 &&
        Math.sign(velocityX) !== Math.sign(inputManager.xAxis))
    ) {
      velocityX += 0.2 * inputManager.xAxis;
    }
    const newX = player.x + velocityX * ticker.deltaMS;
    x = clamp(newX, 0, stageWidth);

    const animationState = Math.floor(performance.now() / 500) & 1;
    if (velocityX !== 0) {
      toRight = velocityX > 0;
      player.texture = animationState
        ? playerAssets.hamRunning1.get()
        : playerAssets.hamRunning2.get();
    } else {
      player.texture = animationState
        ? playerAssets.hamStop1.get()
        : playerAssets.hamStop2.get();
    }

    scrollManager.playerX = x;
    scrollManager.playerY = y;
    player.scale.set(
      (charaSize / player.texture.width) * (toRight ? -1 : 1) * 1.25,
      (charaSize / player.texture.height) * 1.25,
    );
  };

  parent.addChild(player);
  ticker.add(onTick);

  const dispose = () => {
    parent.removeChild(player);
    ticker.remove(onTick);
  };

  return dispose;
}
